import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { ManageCampaignService } from 'src/app/private/services/manage-campaign.service';
import { MessageService } from 'primeng/api';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

interface TrackingData {
  id: string;
  campaign_id: string;
  organization_id: string;
  message_id: string;
  email_to: string;
  email_from: string;
  event_type: string;
  status: string;
  bounce_type: string | null;
  bounce_sub_type: string | null;
  complaint_feedback_type: string | null;
  timestamp: string;
  created_at: string;
  updated_at: string;
}

interface Statistics {
  total: number;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  opened: number;
  clicked: number;
  rejected: number;
  uniqueOpened?: number;
  uniqueClicked?: number;
  uniqueDelivered?: number;
}

interface EmailReportData {
  tracking_data: TrackingData[];
  statistics: Statistics;
  queued?: number;
  total_need_to_send?: number;
  percentage_of_completion?: number;
}

@Component({
  selector: 'app-email-report',
  imports: [SharedModule],
  templateUrl: './email-report.component.html',
  styleUrl: './email-report.component.scss'
})
export class EmailReportComponent implements OnInit {
  refreshReport(): void {
    this.loadReportData();
  }
  @Input() campaignId!: string;
  @Input() campaignName!: string;
  @Output() closeDialog = new EventEmitter<void>();

  loading: boolean = false;
  reportData: EmailReportData | null = null;
  filteredData: TrackingData[] = [];
  groupedData: any[] = []; // For showing unique emails with counts
  
  activeTab: string = 'all';
  activeView: string = 'table'; // 'table' or 'graph'

  // Statistics
  statistics: Statistics = {
    total: 0,
    sent: 0,
    delivered: 0,
    bounced: 0,
    complained: 0,
    opened: 0,
    clicked: 0,
    rejected: 0,
    uniqueOpened: 0,
    uniqueClicked: 0,
    uniqueDelivered: 0
  };

  // Campaign progress tracking
  queuedCount: number = 0;
  totalNeedToSend: number = 0;
  percentageOfCompletion: number = 0;

  // Chart Data
  statusChartData: any;
  statusChartOptions: any;
  
  timelineChartData: any;
  timelineChartOptions: any;

  // Table columns
  displayedColumns: any[] = [
    { field: 'email_to', header: 'Email To', sortable: true },
    { field: 'event_type', header: 'Event Type', sortable: true },
    { field: 'status', header: 'Status', sortable: true },
    { field: 'timestamp', header: 'Timestamp', sortable: true }
  ];

  // Columns for grouped view (opened/clicked)
  groupedColumns: any[] = [
    { field: 'email_to', header: 'Email To', sortable: true },
    { field: 'event_count', header: 'Event Count', sortable: true },
    { field: 'first_event', header: 'First Event', sortable: true },
    { field: 'last_event', header: 'Last Event', sortable: true }
  ];

  // Status tabs
  statusTabs = [
    { label: 'All', value: 'all', icon: 'pi pi-list', count: 0 },
    { label: 'Sent', value: 'sent', icon: 'pi pi-send', count: 0 },
    { label: 'Delivered', value: 'delivered', icon: 'pi pi-check-circle', count: 0 },
    { label: 'Opened', value: 'opened', icon: 'pi pi-eye', count: 0 },
    { label: 'Clicked', value: 'clicked', icon: 'pi pi-cursor', count: 0 },
    { label: 'Bounced', value: 'bounced', icon: 'pi pi-exclamation-triangle', count: 0 },
    { label: 'Complained', value: 'complained', icon: 'pi pi-ban', count: 0 },
    { label: 'Rejected', value: 'rejected', icon: 'pi pi-times-circle', count: 0 }
  ];

  constructor(
    private api: ManageCampaignService,
    private messageService: MessageService,
    private localStorage: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.loadReportData();
  }

  loadReportData(): void {
    this.loading = true;
    
    // For now, using mock data. Replace with actual API call

    const payload = {
        campaign_id: this.campaignId,
        organization_id: this.localStorage.getOrganizationId()
    };

    this.api.getEmailCampaignReport(payload).subscribe({
      next: (data: EmailReportData) => {
        this.processReportData(data);
        this.loading = false;
      },
      error: (error) => {
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'Failed to load report data' 
        });
        this.loading = false;
      }
    });

    // Mock data - Replace this with actual API call
    // const mockData: EmailReportData = {
    //   tracking_data: [
    //     {
    //       id: "2",
    //       campaign_id: "761",
    //       organization_id: "1",
    //       message_id: "0100019bdcc8e665-d5384285-6daf-4ef1-a0dd-7f8b40b7d9e6-000000",
    //       email_to: "gauravsinghrathore505@gmail.com",
    //       email_from: "marketing@gravitasai.in",
    //       event_type: "Send",
    //       status: "sent",
    //       bounce_type: null,
    //       bounce_sub_type: null,
    //       complaint_feedback_type: null,
    //       timestamp: "2026-01-20T19:01:52.357Z",
    //       created_at: "2026-01-20T19:01:59.177Z",
    //       updated_at: "2026-01-20T19:01:59.179Z"
    //     },
    //     {
    //       id: "3",
    //       campaign_id: "761",
    //       organization_id: "1",
    //       message_id: "0100019bdcc8e677-b4d84825-3c41-43d1-b893-6a8ad45bf17b-000000",
    //       email_to: "gaurav.rathore@gravitas.ai",
    //       email_from: "marketing@gravitasai.in",
    //       event_type: "Send",
    //       status: "sent",
    //       bounce_type: null,
    //       bounce_sub_type: null,
    //       complaint_feedback_type: null,
    //       timestamp: "2026-01-20T19:01:52.375Z",
    //       created_at: "2026-01-20T19:01:59.177Z",
    //       updated_at: "2026-01-20T19:01:59.179Z"
    //     },
    //     {
    //       id: "4",
    //       campaign_id: "761",
    //       organization_id: "1",
    //       message_id: "0100019bdcc8e654-e198cc80-d65c-4a60-8703-eab19b2aac62-000000",
    //       email_to: "gauravsinghrathorerathore@gmail.com",
    //       email_from: "marketing@gravitasai.in",
    //       event_type: "Send",
    //       status: "sent",
    //       bounce_type: null,
    //       bounce_sub_type: null,
    //       complaint_feedback_type: null,
    //       timestamp: "2026-01-20T19:01:52.340Z",
    //       created_at: "2026-01-20T19:01:59.208Z",
    //       updated_at: "2026-01-20T19:01:59.209Z"
    //     },
    //     {
    //       id: "5",
    //       campaign_id: "761",
    //       organization_id: "1",
    //       message_id: "0100019bdcc8e665-d5384285-6daf-4ef1-a0dd-7f8b40b7d9e6-000000",
    //       email_to: "gauravsinghrathore505@gmail.com",
    //       email_from: "marketing@gravitasai.in",
    //       event_type: "Delivery",
    //       status: "delivered",
    //       bounce_type: null,
    //       bounce_sub_type: null,
    //       complaint_feedback_type: null,
    //       timestamp: "2026-01-20T19:01:53.131Z",
    //       created_at: "2026-01-20T19:01:59.893Z",
    //       updated_at: "2026-01-20T19:01:59.894Z"
    //     },
    //     {
    //       id: "6",
    //       campaign_id: "761",
    //       organization_id: "1",
    //       message_id: "0100019bdcc8e654-e198cc80-d65c-4a60-8703-eab19b2aac62-000000",
    //       email_to: "gauravsinghrathorerathore@gmail.com",
    //       email_from: "marketing@gravitasai.in",
    //       event_type: "Delivery",
    //       status: "delivered",
    //       bounce_type: null,
    //       bounce_sub_type: null,
    //       complaint_feedback_type: null,
    //       timestamp: "2026-01-20T19:01:53.606Z",
    //       created_at: "2026-01-20T19:02:00.221Z",
    //       updated_at: "2026-01-20T19:02:00.221Z"
    //     },
    //     {
    //       id: "7",
    //       campaign_id: "761",
    //       organization_id: "1",
    //       message_id: "0100019bdcc8e677-b4d84825-3c41-43d1-b893-6a8ad45bf17b-000000",
    //       email_to: "gaurav.rathore@gravitas.ai",
    //       email_from: "marketing@gravitasai.in",
    //       event_type: "Delivery",
    //       status: "delivered",
    //       bounce_type: null,
    //       bounce_sub_type: null,
    //       complaint_feedback_type: null,
    //       timestamp: "2026-01-20T19:01:56.133Z",
    //       created_at: "2026-01-20T19:02:02.528Z",
    //       updated_at: "2026-01-20T19:02:02.530Z"
    //     },
    //     {
    //       id: "8",
    //       campaign_id: "761",
    //       organization_id: "1",
    //       message_id: "0100019bdcc8e654-e198cc80-d65c-4a60-8703-eab19b2aac62-000000",
    //       email_to: "gauravsinghrathorerathore@gmail.com",
    //       email_from: "marketing@gravitasai.in",
    //       event_type: "Open",
    //       status: "opened",
    //       bounce_type: null,
    //       bounce_sub_type: null,
    //       complaint_feedback_type: null,
    //       timestamp: "2026-01-20T19:01:57.231Z",
    //       created_at: "2026-01-20T19:02:03.781Z",
    //       updated_at: "2026-01-20T19:02:03.782Z"
    //     },
    //     {
    //       id: "9",
    //       campaign_id: "761",
    //       organization_id: "1",
    //       message_id: "0100019bdcc8e654-e198cc80-d65c-4a60-8703-eab19b2aac62-000000",
    //       email_to: "gauravsinghrathorerathore@gmail.com",
    //       email_from: "marketing@gravitasai.in",
    //       event_type: "Open",
    //       status: "opened",
    //       bounce_type: null,
    //       bounce_sub_type: null,
    //       complaint_feedback_type: null,
    //       timestamp: "2026-01-20T19:04:33.764Z",
    //       created_at: "2026-01-20T19:04:40.280Z",
    //       updated_at: "2026-01-20T19:04:40.282Z"
    //     }
    //   ],
    //   statistics: {
    //     total: 8,
    //     sent: 3,
    //     delivered: 3,
    //     bounced: 0,
    //     complained: 0,
    //     opened: 2,
    //     clicked: 0,
    //     rejected: 0
    //   }
    // };

    // setTimeout(() => {
    //   this.processReportData(mockData);
    //   this.loading = false;
    // }, 500);
  }

  processReportData(data: EmailReportData): void {
    this.reportData = data;
    this.statistics = data.statistics;
    this.filteredData = data.tracking_data;
    
    // Set campaign progress data
    this.queuedCount = data.queued || 0;
    this.totalNeedToSend = data.total_need_to_send || 0;
    this.percentageOfCompletion = data.percentage_of_completion || 0;
    
    // Calculate unique email counts for delivered, opened and clicked
    const uniqueDeliveredEmails = new Set(
      data.tracking_data
        .filter(item => item.status.toLowerCase() === 'delivered')
        .map(item => item.email_to)
    );
    
    const uniqueOpenedEmails = new Set(
      data.tracking_data
        .filter(item => item.status.toLowerCase() === 'opened')
        .map(item => item.email_to)
    );
    
    const uniqueClickedEmails = new Set(
      data.tracking_data
        .filter(item => item.status.toLowerCase() === 'clicked')
        .map(item => item.email_to)
    );
    
    this.statistics.uniqueDelivered = uniqueDeliveredEmails.size;
    this.statistics.uniqueOpened = uniqueOpenedEmails.size;
    this.statistics.uniqueClicked = uniqueClickedEmails.size;
    
    // Update tab counts - use unique counts for delivered, opened and clicked
    this.statusTabs.forEach(tab => {
      if (tab.value === 'all') {
        tab.count = data.statistics.total;
      } else if (tab.value === 'delivered') {
        tab.count = this.statistics.uniqueDelivered || 0;
      } else if (tab.value === 'opened') {
        tab.count = this.statistics.uniqueOpened || 0;
      } else if (tab.value === 'clicked') {
        tab.count = this.statistics.uniqueClicked || 0;
      } else {
        tab.count = data.statistics[tab.value as keyof Statistics] || 0;
      }
    });

    this.initializeCharts();
    this.filterDataByStatus(this.activeTab);
  }

  filterDataByStatus(status: string): void {
    this.activeTab = status;
    
    if (!this.reportData) return;
    
    if (status === 'all') {
      this.filteredData = this.reportData.tracking_data;
      this.groupedData = [];
    } else if (status === 'opened' || status === 'clicked') {
      // For opened and clicked, show unique emails with counts
      const statusData = this.reportData.tracking_data.filter(
        item => item.status.toLowerCase() === status.toLowerCase()
      );
      
      // Group by email
      const emailGroups = new Map<string, TrackingData[]>();
      statusData.forEach(item => {
        if (!emailGroups.has(item.email_to)) {
          emailGroups.set(item.email_to, []);
        }
        emailGroups.get(item.email_to)!.push(item);
      });
      
      // Create grouped data
      this.groupedData = Array.from(emailGroups.entries()).map(([email, events]) => {
        const sortedEvents = events.sort((a, b) => 
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        return {
          email_to: email,
          event_count: events.length,
          first_event: sortedEvents[0].timestamp,
          last_event: sortedEvents[sortedEvents.length - 1].timestamp,
          status: status
        };
      });
      
      this.filteredData = [];
    } else {
      this.filteredData = this.reportData.tracking_data.filter(
        item => item.status.toLowerCase() === status.toLowerCase()
      );
      this.groupedData = [];
    }
  }

  initializeCharts(): void {
    // Status Distribution Pie Chart
    this.statusChartData = {
      labels: ['Sent', 'Delivered', 'Opened (Unique)', 'Clicked (Unique)', 'Bounced', 'Complained', 'Rejected'],
      datasets: [{
        data: [
          this.statistics.sent,
          this.statistics.delivered,
          this.statistics.uniqueOpened || 0,
          this.statistics.uniqueClicked || 0,
          this.statistics.bounced,
          this.statistics.complained,
          this.statistics.rejected
        ],
        backgroundColor: [
          '#3B82F6',
          '#10B981',
          '#8B5CF6',
          '#F59E0B',
          '#EF4444',
          '#DC2626',
          '#6B7280'
        ],
        borderWidth: 0
      }]
    };

    this.statusChartOptions = {
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 12
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const value = context.parsed || 0;
              const total = this.statistics.total;
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
              return `${label}: ${value} (${percentage}%)`;
            }
          }
        }
      }
    };

    // Timeline Bar Chart
    this.timelineChartData = {
      labels: ['Sent', 'Delivered', 'Opened (Unique)', 'Clicked (Unique)', 'Bounced', 'Complained', 'Rejected'],
      datasets: [{
        label: 'Event Count',
        data: [
          this.statistics.sent,
          this.statistics.delivered,
          this.statistics.uniqueOpened || 0,
          this.statistics.uniqueClicked || 0,
          this.statistics.bounced,
          this.statistics.complained,
          this.statistics.rejected
        ],
        backgroundColor: '#3B82F6',
        borderRadius: 8
      }]
    };

    this.timelineChartOptions = {
      indexAxis: 'y',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              return `Count: ${context.parsed.x}`;
            }
          }
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            stepSize: 1
          }
        }
      }
    };
  }

  switchView(view: string): void {
    this.activeView = view;
  }

  getStatusBadgeClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      'sent': 'status-sent',
      'delivered': 'status-delivered',
      'opened': 'status-opened',
      'clicked': 'status-clicked',
      'bounced': 'status-bounced',
      'complained': 'status-complained',
      'rejected': 'status-rejected'
    };
    return statusMap[status.toLowerCase()] || 'status-default';
  }

  formatTimestamp(timestamp: string): string {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  isGroupedView(): boolean {
    return this.activeTab === 'opened' || this.activeTab === 'clicked';
  }

  getDisplayColumns(): any[] {
    return this.isGroupedView() ? this.groupedColumns : this.displayedColumns;
  }

  getDisplayData(): any[] {
    return this.isGroupedView() ? this.groupedData : this.filteredData;
  }

  calculateEngagementRate(): number {
    if (this.statistics.delivered === 0) return 0;
    return (((this.statistics.uniqueOpened || 0) / this.statistics.delivered) * 100);
  }

  calculateClickRate(): number {
    const uniqueOpened = this.statistics.uniqueOpened || 0;
    if (uniqueOpened === 0) return 0;
    return (((this.statistics.uniqueClicked || 0) / uniqueOpened) * 100);
  }

  calculateBounceRate(): number {
    if (this.statistics.total === 0) return 0;
    return ((this.statistics.bounced / this.statistics.total) * 100);
  }

  close(): void {
    this.closeDialog.emit();
  }
}
